import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

const KNOWLEDGE_TYPES = [
  { id: 'material', label: 'Material / Fabric', description: 'Fabric technologies, materials, care instructions' },
  { id: 'policy', label: 'Policy', description: 'Return policies, trade-in rules, warranties' },
  { id: 'faq', label: 'FAQ', description: 'Frequently asked questions and answers' },
  { id: 'training', label: 'Training', description: 'General training content, guides' },
  { id: 'custom', label: 'Custom', description: 'Other knowledge topics' },
];

export const Route = createFileRoute('/knowledge_/new')({
  component: NewKnowledgePage,
});

function NewKnowledgePage() {
  const navigate = useNavigate();
  const [type, setType] = useState('');
  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async (publish = true) => {
    if (!type || !title) {
      setError('Type and title are required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/v1/knowledge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          category: category || undefined,
          title,
          content,
          tags: tags.split(',').map(t => t.trim()).filter(Boolean),
          isPublished: publish,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        navigate({ to: '/knowledge/$id', params: { id: data.id } });
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save');
      }
    } catch (err) {
      setError('Failed to save knowledge item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate({ to: '/knowledge' })}
          className="text-sm flex items-center gap-1 mb-4"
          style={{ color: '#A9A9A9' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M15 18L9 12L15 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Back to Knowledge Hub
        </button>
        <h1 className="text-2xl font-light" style={{ color: '#171717' }}>
          Add Knowledge
        </h1>
        <p className="text-sm" style={{ color: '#A9A9A9' }}>
          Add content that your AI Educator can reference when helping customers
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4" style={{ backgroundColor: 'rgba(255,76,0,0.1)', color: '#FF4C00' }}>
          {error}
        </div>
      )}

      {/* Type Selection */}
      <div className="mb-6">
        <label className="block text-xs uppercase tracking-wider mb-3" style={{ color: '#A9A9A9' }}>
          Knowledge Type
        </label>
        <div className="grid grid-cols-3 gap-3">
          {KNOWLEDGE_TYPES.map((t) => (
            <button
              key={t.id}
              onClick={() => setType(t.id)}
              className="p-4 border text-left transition-all"
              style={{
                borderColor: type === t.id ? '#00FBA9' : 'rgba(169,169,169,0.2)',
                borderWidth: type === t.id ? '2px' : '1px',
                backgroundColor: type === t.id ? 'rgba(0,251,169,0.05)' : '#FFFFFF',
              }}
            >
              <p className="font-medium text-sm" style={{ color: '#171717' }}>{t.label}</p>
              <p className="text-xs mt-1" style={{ color: '#A9A9A9' }}>{t.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white border p-6" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
        <div className="space-y-6">
          {/* Title */}
          <div>
            <label className="block text-xs uppercase tracking-wider mb-2" style={{ color: '#A9A9A9' }}>
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 border text-sm"
              style={{ borderColor: 'rgba(169,169,169,0.3)' }}
              placeholder="e.g., Nulu™ Fabric Technology"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs uppercase tracking-wider mb-2" style={{ color: '#A9A9A9' }}>
              Category (Optional)
            </label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-3 border text-sm"
              style={{ borderColor: 'rgba(169,169,169,0.3)' }}
              placeholder="e.g., Fabrics, Programs, Help"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-xs uppercase tracking-wider mb-2" style={{ color: '#A9A9A9' }}>
              Content (Markdown supported)
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full px-4 py-3 border text-sm font-mono min-h-[300px]"
              style={{ borderColor: 'rgba(169,169,169,0.3)' }}
              placeholder={`# Title

Write your content here using Markdown formatting.

## Section Header

- Bullet point 1
- Bullet point 2

**Bold text** and *italic text* are supported.`}
            />
            <p className="text-xs mt-2" style={{ color: '#A9A9A9' }}>
              Use Markdown for formatting: # headers, **bold**, *italic*, - lists
            </p>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs uppercase tracking-wider mb-2" style={{ color: '#A9A9A9' }}>
              Tags (comma separated)
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className="w-full px-4 py-3 border text-sm"
              style={{ borderColor: 'rgba(169,169,169,0.3)' }}
              placeholder="e.g., yoga, soft, lightweight"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center mt-8 pt-6 border-t" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="px-4 py-2 rounded-full text-sm"
            style={{ color: '#A9A9A9' }}
          >
            Save as Draft
          </button>
          <div className="flex gap-3">
            <button
              onClick={() => navigate({ to: '/knowledge' })}
              className="px-4 py-2 rounded-full text-sm"
              style={{ color: '#A9A9A9' }}
            >
              Cancel
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={saving || !type || !title}
              className="px-6 py-2 rounded-full text-sm font-medium disabled:opacity-50"
              style={{ backgroundColor: '#00FBA9', color: '#171717' }}
            >
              {saving ? 'Saving...' : 'Save & Index'}
            </button>
          </div>
        </div>
      </div>

      {/* Preview Panel */}
      {content && (
        <div className="mt-6 bg-white border p-6" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
          <h3 className="text-xs uppercase tracking-wider mb-4" style={{ color: '#A9A9A9' }}>
            Preview
          </h3>
          <div 
            className="prose prose-sm max-w-none"
            style={{ color: '#171717' }}
            dangerouslySetInnerHTML={{ __html: simpleMarkdown(content) }}
          />
        </div>
      )}
    </div>
  );
}

// Simple markdown renderer (basic support)
function simpleMarkdown(text: string): string {
  return text
    .replace(/^### (.*$)/gm, '<h3 class="text-lg font-medium mt-4 mb-2">$1</h3>')
    .replace(/^## (.*$)/gm, '<h2 class="text-xl font-medium mt-6 mb-3">$1</h2>')
    .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-medium mt-6 mb-3">$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^- (.*$)/gm, '<li class="ml-4">$1</li>')
    .replace(/(<li.*<\/li>)/s, '<ul class="list-disc mb-4">$1</ul>')
    .replace(/\n\n/g, '</p><p class="mb-3">')
    .replace(/^(.+)$/gm, (match) => {
      if (match.startsWith('<')) return match;
      return `<p class="mb-3">${match}</p>`;
    });
}


